import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { saveChartPreferences, loadChartPreferences } from "utils/chartPreferences";
import useViewport from "hooks/useViewport";
import {
  subscribeChartWidget,
  isChartReady,
  currentResolution,
  setResolution,
  subscribeResolutionChange,
  takeScreenshot,
  zoomIn,
  zoomOut,
  CHART_STYLE_OPTIONS,
  currentChartType,
  setChartType,
  subscribeChartTypeChange,
  chartPrefFromStyle,
  chartStyleFromPref,
  getVolumeMode,
  setVolumeMode,
} from "./chartToolBridge";
import SelectIndicatorDialog from "./SelectIndicatorDialog";

/** MT5 quick chart types — desktop shows 4; mobile uses one dropdown. */
const QUICK_STYLES = [
  { id: 0, title: "Bars" },
  { id: 1, title: "Candles" },
  { id: 3, title: "Area" },
  { id: 2, title: "Line" },
];

/** MT5 mobile chart-type menu — text only (Bars / Candlestick / Area / Line). */
const MT5_STYLE_OPTIONS = [
  { id: 0, label: "Bars" },
  { id: 1, label: "Candlestick" },
  { id: 3, label: "Area" },
  { id: 2, label: "Line" },
];

const VOLUME_OPTIONS = [
  { id: "volumes", label: "Volumes" },
  { id: "tick", label: "Tick Volumes" },
  { id: "none", label: "Without Volumes" },
];

/**
 * MT5: check only on the selected row (no empty slot on others).
 * Menu width = longest label; selecting that long label adds the tick and can grow width.
 */
function Mt5MenuCheck({ show }) {
  if (!show) return null;
  return (
    <span className="mt5-header-menu__check" aria-hidden>
      <svg viewBox="0 0 16 16" width="14" height="14">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 8.2l3.2 3.2L13 4.4"
        />
      </svg>
    </span>
  );
}

/** MT5: dropdown width = content; keep fully on-screen under the trigger. */
function placeHeaderMenu(anchorEl, menuEl) {
  if (!anchorEl) return null;
  const r = anchorEl.getBoundingClientRect();
  const pad = 8;
  const menuW = Math.max(menuEl?.offsetWidth || 0, 96);
  let left = Math.round(r.left);
  if (left + menuW > window.innerWidth - pad) {
    left = Math.max(pad, Math.round(window.innerWidth - menuW - pad));
  }
  if (left < pad) left = pad;
  return { top: Math.round(r.bottom + 4), left };
}

/** Icons aligned with TradingView Advanced Charts chart-style menu. */
function ChartStyleIcon({ type, size = 28 }) {
  const common = {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 28 28",
    width: size,
    height: size,
    fill: "currentColor",
    "aria-hidden": true,
  };
  if (type === 0) {
    return (
      <svg {...common}>
        <path d="M9.5 6h1.2v3.2H13v1.2H10.7v7.2H13v1.2H10.7V22H9.5v-3.2H7.2V17.6h2.3V10.4H7.2V9.2h2.3V6Zm8 3.2h1.2V12H21v1.2h-2.3v4.8H21V19h-2.3V22h-1.2v-3h-2.3v-1.2h2.3v-4.8h-2.3V12h2.3V9.2Z" />
      </svg>
    );
  }
  if (type === 1 || type === 19) {
    return (
      <svg {...common}>
        <path d="M9 6h1.2v3H13.5v9H10.2v3H9v-3H5.7v-9H9V6Zm9.5 2.5h1.2v2.5H23v7h-3.3v2.5h-1.2V18H15.5v-7h3.3V8.5Z" />
      </svg>
    );
  }
  if (type === 2) {
    return (
      <svg {...common}>
        <path d="M6.2 17.3l5.1-5.1 3.4 3.4 7.1-7.1.85.85-7.95 7.95-3.4-3.4-4.25 4.25-.85-.85z" />
      </svg>
    );
  }
  if (type === 3) {
    return (
      <svg {...common}>
        <path opacity="0.35" d="M5 19.5V10l4.2 3.6 3.8-6.2 4.4 5.2 5.6-3.8V19.5H5Z" />
        <path d="M5 10l4.2 3.6 3.8-6.2 4.4 5.2 5.6-3.8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === 9) {
    return (
      <svg {...common}>
        <path fillRule="evenodd" d="M9 6h1.2v3H13.5v9H10.2v3H9v-3H5.7v-9H9V6Zm1.2 4.2H6.9v6.6h3.3V10.2Zm9.5-1.7h1.2v2.5H24v7h-3.1v2.5h-1.2V18H16.6v-7h3.1V8.5Zm0 3.7h-2.1v4.6h2.1v-4.6Z" />
      </svg>
    );
  }
  if (type === 10) {
    return (
      <svg {...common}>
        <path opacity="0.4" d="M4.5 14h19v1.2h-19z" />
        <path d="M5 17l4.5-6.2 3.4 3.3 4.2-5.6 6.4 4.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === 12) {
    return (
      <svg {...common}>
        <path d="M7 8h14v1.3H15.2V19H12.8V9.3H7V8Zm4.5 3.5h5v1.2h-5v-1.2Zm0 3h5v1.2h-5v-1.2Z" />
      </svg>
    );
  }
  if (type === 13) {
    return (
      <svg {...common}>
        <path d="M6.5 12.5h3.4V21H6.5v-8.5Zm5.8-5.5h3.4V21h-3.4V7Zm5.8 3.5h3.4V21h-3.4V10.5Z" />
      </svg>
    );
  }
  if (type === 14) {
    return (
      <svg {...common}>
        <path d="M6.2 17.3l5.1-5.1 3.4 3.4 7.1-7.1.85.85-7.95 7.95-3.4-3.4-4.25 4.25-.85-.85z" />
        <circle cx="11.3" cy="12.2" r="1.35" />
        <circle cx="14.7" cy="15.6" r="1.35" />
        <circle cx="21.9" cy="8.5" r="1.35" />
      </svg>
    );
  }
  if (type === 15) {
    return (
      <svg {...common}>
        <path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" d="M5 18.5h4.5V12h4.2v6.5H18V8.5h5" />
      </svg>
    );
  }
  if (type === 16) {
    return (
      <svg {...common}>
        <path opacity="0.3" d="M5 19.5V12l4 2.5 4-5.5 5 4 5-3v9.5H5Z" />
        <path d="M5 12l4 2.5 4-5.5 5 4 5-3" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <path d="M5 15.5l4-1 4 2.5 5-2.2 5 1.2" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M9 6h1.2v3H13.5v9H10.2v3H9v-3H5.7v-9H9V6Zm9.5 2.5h1.2v2.5H23v7h-3.3v2.5h-1.2V18H15.5v-7h3.3V8.5Z" />
    </svg>
  );
}

/** Shared MT5 wave path used by Area + Line icons. */
const MT5_WAVE = "M2.8 15.8C5.2 15.8 6.2 8.2 9 8.2S12.6 16.8 15.2 14.2 19.2 7.4 21.4 9.2";

/** MT5 header glyphs — Bars · Candles · Area · Line (match MT5 web exactly). */
function Mt5QuickIcon({ type, size = 22 }) {
  const common = {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    width: size,
    height: size,
    "aria-hidden": true,
  };
  if (type === 0) {
    // Two OHLC bars (open tick left, close tick right)
    return (
      <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
        <path d="M8 4.5v15" />
        <path d="M4.8 16.5H8" />
        <path d="M8 9.2h3.2" />
        <path d="M16 5.5v14" />
        <path d="M12.8 12.2H16" />
        <path d="M16 8h3.2" />
      </svg>
    );
  }
  if (type === 1) {
    // Dual candles: solid left + hollow right (MT5)
    return (
      <svg {...common}>
        <path
          fill="currentColor"
          d="M8.2 3.2h1.5v3.3h2.9v11H9.7v3.3H8.2v-3.3H5.3V6.5h2.9V3.2z"
        />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.55"
          d="M16.05 3.8v3M16.05 17.2v3"
        />
        <rect
          x="13.15"
          y="6.8"
          width="5.8"
          height="10.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.55"
        />
      </svg>
    );
  }
  if (type === 3) {
    // Area — same wave + fill under curve
    return (
      <svg {...common}>
        <path
          fill="currentColor"
          opacity="0.28"
          d={`${MT5_WAVE}L21.4 19.2H2.8Z`}
        />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.55"
          strokeLinecap="round"
          strokeLinejoin="round"
          d={MT5_WAVE}
        />
      </svg>
    );
  }
  // Line — same wave, no fill
  return (
    <svg {...common}>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.55"
        strokeLinecap="round"
        strokeLinejoin="round"
        d={MT5_WAVE}
      />
    </svg>
  );
}

/** Header chip uses shortLabel; MT5 dropdown uses full menuLabel. */
const INTERVALS = [
  { res: "1", label: "M1", menuLabel: "1 Minute" },
  { res: "5", label: "M5", menuLabel: "5 Minutes" },
  { res: "15", label: "M15", menuLabel: "15 Minutes" },
  { res: "30", label: "M30", menuLabel: "30 Minutes" },
  { res: "60", label: "H1", menuLabel: "1 Hour" },
  { res: "240", label: "H4", menuLabel: "4 Hours" },
  { res: "1D", label: "D1", menuLabel: "Daily" },
  { res: "1W", label: "W1", menuLabel: "Weekly" },
  { res: "1M", label: "MN", menuLabel: "Monthly" },
];

export default function ChartHeaderControls({
  tradeSlot = null,
  trailing = null,
  marketWatchHidden = false,
  onToggleMarketWatch = null,
} = {}) {
  const { width, isMobile: isMobileViewport } = useViewport();
  const compactTimeframes = isMobileViewport || width <= 1200;
  const compactChartTypes = isMobileViewport || width <= 1100;
  const showZoom = !isMobileViewport && width > 1180;
  const showEndIcons = !isMobileViewport && width > 1020;
  const [res, setRes] = useState("1");
  const [chartStyle, setChartStyle] = useState(
    () => chartStyleFromPref(loadChartPreferences().defaultChartType) || 1
  );
  const [styleOpen, setStyleOpen] = useState(false);
  const [volOpen, setVolOpen] = useState(false);
  const [volumeMode, setVolumeModeState] = useState("volumes");
  const [tfOpen, setTfOpen] = useState(false);
  const [indicatorOpen, setIndicatorOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [volMenuPos, setVolMenuPos] = useState({ top: 0, left: 0 });
  const [tfMenuPos, setTfMenuPos] = useState({ top: 0, left: 0 });
  const styleRef = useRef(null);
  const btnRef = useRef(null);
  const volRef = useRef(null);
  const volBtnRef = useRef(null);
  const tfRef = useRef(null);
  const tfBtnRef = useRef(null);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const sync = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    sync();
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen?.();
      } else {
        const el = document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      }
    } catch {
      /* browser may block without gesture / policy */
    }
  }, []);

  useEffect(() => {
    let unsubInterval = () => {};
    let unsubStyle = () => {};
    let poll = null;

    const attach = () => {
      if (!isChartReady()) return false;
      const now = currentResolution();
      if (!now) return false;
      setRes(now);
      setChartStyle(currentChartType());
      getVolumeMode().then((m) => setVolumeModeState(m)).catch(() => {});
      unsubInterval();
      unsubInterval = subscribeResolutionChange((interval) => setRes(interval));
      unsubStyle();
      unsubStyle = subscribeChartTypeChange((type) => {
        setChartStyle(type);
        saveChartPreferences({ defaultChartType: chartPrefFromStyle(type) });
      });
      return true;
    };

    const unsubWidget = subscribeChartWidget((w) => {
      unsubInterval();
      unsubInterval = () => {};
      unsubStyle();
      unsubStyle = () => {};
      if (!w) return;
      if (!attach() && !poll) {
        poll = setInterval(() => {
          if (attach()) {
            clearInterval(poll);
            poll = null;
          }
        }, 300);
      }
    });

    return () => {
      if (poll) clearInterval(poll);
      unsubInterval();
      unsubStyle();
      unsubWidget();
    };
  }, []);

  useLayoutEffect(() => {
    if (!compactTimeframes || !tfOpen || !tfBtnRef.current) return undefined;
    const place = () => {
      const next = placeHeaderMenu(tfBtnRef.current, tfRef.current);
      if (next) setTfMenuPos(next);
    };
    place();
    // Re-measure after paint so width follows longest option text (MT5).
    const raf = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
    // Re-place when selection changes: longest label + tick can widen the menu (MT5).
  }, [tfOpen, compactTimeframes, res]);

  useLayoutEffect(() => {
    if (!volOpen || !volBtnRef.current) return undefined;
    const place = () => {
      const next = placeHeaderMenu(volBtnRef.current, volRef.current);
      if (next) setVolMenuPos(next);
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [volOpen, volumeMode]);

  useLayoutEffect(() => {
    if (!styleOpen || !btnRef.current) return undefined;
    const place = () => {
      const next = placeHeaderMenu(btnRef.current, styleRef.current);
      if (next) setMenuPos(next);
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [styleOpen, chartStyle]);

  useEffect(() => {
    if (!styleOpen) return undefined;
    const onDoc = (e) => {
      if (!styleRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) {
        setStyleOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setStyleOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [styleOpen]);

  useEffect(() => {
    if (!volOpen) return undefined;
    const onDoc = (e) => {
      if (!volRef.current?.contains(e.target) && !volBtnRef.current?.contains(e.target)) {
        setVolOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setVolOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [volOpen]);

  useEffect(() => {
    if (!compactTimeframes && tfOpen) setTfOpen(false);
  }, [compactTimeframes, tfOpen]);

  useEffect(() => {
    if (!compactTimeframes || !tfOpen) return undefined;
    const onDoc = (e) => {
      if (!tfRef.current?.contains(e.target) && !tfBtnRef.current?.contains(e.target)) {
        setTfOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setTfOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [tfOpen, compactTimeframes]);

  const pick = useCallback(async (next) => {
    setRes(next);
    setTfOpen(false);
    await setResolution(next);
  }, []);

  const pickStyle = useCallback(async (nextId) => {
    setChartStyle(nextId);
    setStyleOpen(false);
    await setChartType(nextId);
    saveChartPreferences({ defaultChartType: chartPrefFromStyle(nextId) });
  }, []);

  const pickVolume = useCallback(async (mode) => {
    setVolumeModeState(mode);
    setVolOpen(false);
    await setVolumeMode(mode);
    saveChartPreferences({ showVolume: mode !== "none" });
  }, []);

  const styleLabel =
    MT5_STYLE_OPTIONS.find((t) => t.id === chartStyle)?.label ||
    CHART_STYLE_OPTIONS.find((t) => t.id === chartStyle)?.label ||
    "Candlestick";
  const tfItem = INTERVALS.find((i) => i.res === res);
  const tfLabel = tfItem?.label || res || "M1";
  const tfMenuLabel = tfItem?.menuLabel || tfLabel;
  const quickActiveId = QUICK_STYLES.some((q) => q.id === chartStyle) ? chartStyle : null;

  const isDark =
    typeof document !== "undefined" &&
    !!document.querySelector(".trading-platform.dark");

  /* Mobile: MT5 white text-only menus. Desktop style menu keeps TV icons. */
  const styleMenu =
    styleOpen && typeof document !== "undefined"
      ? createPortal(
          isMobileViewport ? (
            <ul
              ref={styleRef}
              className={`mt5-header-menu${isDark ? " mt5-header-menu--dark" : ""}`}
              role="listbox"
              aria-label="Chart type"
              style={{ top: menuPos.top, left: menuPos.left, width: "max-content" }}
            >
              {MT5_STYLE_OPTIONS.map((opt) => {
                const active = chartStyle === opt.id;
                return (
                  <li key={opt.id} role="none">
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`mt5-header-menu__item${active ? " is-active" : ""}`}
                      onClick={() => pickStyle(opt.id)}
                    >
                      <span className="mt5-header-menu__label">{opt.label}</span>
                      <Mt5MenuCheck show={active} />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <ul
              ref={styleRef}
              className={`tv-chart-style-menu${isDark ? " tv-chart-style-menu--dark" : ""}`}
              role="listbox"
              aria-label="Chart type"
              style={{ top: menuPos.top, left: menuPos.left }}
            >
              {CHART_STYLE_OPTIONS.map((opt) => {
                const active = chartStyle === opt.id;
                return (
                  <React.Fragment key={opt.key}>
                    <li role="none">
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={`tv-chart-style-menu__item${active ? " is-active" : ""}`}
                        onClick={() => pickStyle(opt.id)}
                      >
                        <span className="tv-chart-style-menu__icon">
                          <ChartStyleIcon type={opt.id} />
                        </span>
                        <span className="tv-chart-style-menu__label">{opt.label}</span>
                      </button>
                    </li>
                    {opt.sep ? <li className="tv-chart-style-menu__sep" role="separator" /> : null}
                  </React.Fragment>
                );
              })}
            </ul>
          ),
          document.body
        )
      : null;

  const volMenu =
    volOpen && typeof document !== "undefined"
      ? createPortal(
          <ul
            ref={volRef}
            className={`mt5-header-menu${isDark ? " mt5-header-menu--dark" : ""}`}
            role="listbox"
            aria-label="Volumes"
            style={{ top: volMenuPos.top, left: volMenuPos.left, width: "max-content" }}
          >
            {VOLUME_OPTIONS.map((opt) => {
              const active = volumeMode === opt.id;
              return (
                <li key={opt.id} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`mt5-header-menu__item${active ? " is-active" : ""}`}
                    onClick={() => pickVolume(opt.id)}
                  >
                    <span className="mt5-header-menu__label">{opt.label}</span>
                    <Mt5MenuCheck show={active} />
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body
        )
      : null;

  const tfMenu =
    compactTimeframes && tfOpen && typeof document !== "undefined"
      ? createPortal(
          <ul
            ref={tfRef}
            className={`mt5-header-menu mt5-header-menu--tf${isDark ? " mt5-header-menu--dark" : ""}`}
            role="listbox"
            aria-label="Timeframe"
            style={{ top: tfMenuPos.top, left: tfMenuPos.left, width: "max-content" }}
          >
            {INTERVALS.map(({ res: r, label, menuLabel }) => {
              const active = res === r;
              return (
                <li key={r} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`mt5-header-menu__item${active ? " is-active" : ""}`}
                    onClick={() => pick(r)}
                  >
                    <span className="mt5-header-menu__label">{menuLabel || label}</span>
                    <Mt5MenuCheck show={active} />
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body
        )
      : null;

  const moreActive = quickActiveId == null;
  /** Icon shown on mobile single chart-type control (Bars / Candles / other). */
  const mobileStyleIconId = QUICK_STYLES.some((q) => q.id === chartStyle)
    ? chartStyle
    : chartStyle === 0 || chartStyle === 1
      ? chartStyle
      : 1;

  return (
    <div
      className={`chart-head chart-head--mt5${isMobileViewport ? " chart-head--mt5-mobile" : ""}${!isMobileViewport && compactTimeframes ? " chart-head--mt5-compact" : ""}`}
      role="toolbar"
      aria-label="Chart controls"
    >
      {/* Mobile MT5: one chart-type ▾ · Volume · TF · Order  |  Desktop: Bars(+▾) · Candles · Area · Line */}
      <div className="chart-head__styles" role="group" aria-label="Chart type">
        {compactChartTypes ? (
          <div className="chart-head__style-first chart-head__style-first--single">
            <button
              ref={btnRef}
              type="button"
              className={`chart-head__btn chart-head__btn--icon chart-head__btn--quick chart-head__btn--style-dd chart-head__btn--has-menu${styleOpen ? " is-open" : ""}`}
              title={`Chart type: ${styleLabel}`}
              aria-label={`Chart type: ${styleLabel}`}
              aria-haspopup="listbox"
              aria-expanded={styleOpen}
              onClick={() => {
                setVolOpen(false);
                setTfOpen(false);
                setStyleOpen((o) => !o);
              }}
            >
              <Mt5QuickIcon type={mobileStyleIconId} size={22} />
              <span className="chart-head__menu-mark" aria-hidden />
            </button>
            {styleMenu}
          </div>
        ) : (
          QUICK_STYLES.map((q, idx) => {
            const active = quickActiveId === q.id;
            if (idx === 0) {
              return (
                <div key={q.id} className="chart-head__style-first">
                  <button
                    type="button"
                    className={`chart-head__btn chart-head__btn--icon chart-head__btn--quick${active ? " is-active" : ""}`}
                    title={q.title}
                    aria-label={q.title}
                    aria-pressed={active}
                    onClick={() => {
                      setVolOpen(false);
                      setTfOpen(false);
                      pickStyle(q.id);
                    }}
                  >
                    <Mt5QuickIcon type={q.id} size={24} />
                  </button>
                  <button
                    ref={btnRef}
                    type="button"
                    className={`chart-head__btn chart-head__btn--caret chart-head__btn--option${styleOpen ? " is-open" : ""}${moreActive ? " is-active" : ""}`}
                    title={`More chart types (${styleLabel})`}
                    aria-label="More chart types"
                    aria-haspopup="listbox"
                    aria-expanded={styleOpen}
                    onClick={() => {
                      setVolOpen(false);
                      setTfOpen(false);
                      setStyleOpen((o) => !o);
                    }}
                  >
                    <svg viewBox="0 0 12 12" width="8" height="8" aria-hidden>
                      <path fill="currentColor" d="M2.2 4.2a.75.75 0 0 1 1.06 0L6 6.94l2.74-2.74a.75.75 0 1 1 1.06 1.06L6.53 8.53a.75.75 0 0 1-1.06 0L2.2 5.26a.75.75 0 0 1 0-1.06z" />
                    </svg>
                  </button>
                  {styleMenu}
                </div>
              );
            }
            return (
              <button
                key={q.id}
                type="button"
                className={`chart-head__btn chart-head__btn--icon chart-head__btn--quick${active ? " is-active" : ""}`}
                title={q.title}
                aria-label={q.title}
                aria-pressed={active}
                onClick={() => {
                  setVolOpen(false);
                  setStyleOpen(false);
                  pickStyle(q.id);
                }}
              >
                <Mt5QuickIcon type={q.id} size={24} />
              </button>
            );
          })
        )}
      </div>

      <span className="chart-head__sep" aria-hidden />

      <div className="chart-head__volume">
        <button
          ref={volBtnRef}
          type="button"
          className={`chart-head__btn chart-head__btn--volume chart-head__btn--has-menu${volOpen ? " is-open" : ""}${volumeMode !== "none" ? " is-active" : ""}`}
          title="Volumes"
          aria-label="Volumes"
          aria-haspopup="listbox"
          aria-expanded={volOpen}
          onClick={() => {
            setStyleOpen(false);
            setTfOpen(false);
            setVolOpen((o) => !o);
          }}
        >
          {/* MT5 volumes: rising bars + tick/X mark */}
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
            <path
              fill="currentColor"
              d="M3 16.2h2v4.3H3v-4.3zm3.6-3.2h2v7.5h-2v-7.5zm3.6-3h2v10.5h-2V10zm3.6-2.6h2v13.1h-2V7.4zm3.6 1.5h2v11.6h-2V8.9z"
            />
            <path
              fill="currentColor"
              d="M18.2 4.2l1.15 1.15 2.35-2.35.95.95-3.3 3.3-2.1-2.1.95-.95z"
            />
          </svg>
          {isMobileViewport ? <span className="chart-head__menu-mark" aria-hidden /> : (
            <svg className="chart-head__vol-caret" viewBox="0 0 12 12" width="8" height="8" aria-hidden>
              <path fill="currentColor" d="M2.2 4.2a.75.75 0 0 1 1.06 0L6 6.94l2.74-2.74a.75.75 0 1 1 1.06 1.06L6.53 8.53a.75.75 0 0 1-1.06 0L2.2 5.26a.75.75 0 0 1 0-1.06z" />
            </svg>
          )}
        </button>
        {volMenu}
      </div>

      <span className="chart-head__sep" aria-hidden />

      {compactTimeframes ? (
        <div className="chart-head__tf-dd">
          <button
            ref={tfBtnRef}
            type="button"
            className={`chart-head__btn chart-head__btn--tf chart-head__btn--has-menu${tfOpen ? " is-open" : ""}`}
            title={tfMenuLabel}
            aria-label={tfMenuLabel}
            aria-haspopup="listbox"
            aria-expanded={tfOpen}
            onClick={() => {
              setStyleOpen(false);
              setVolOpen(false);
              setTfOpen((o) => !o);
            }}
          >
            {/* Mobile/compact: short chip (H1) — width follows this text; menu uses full labels */}
            <span className="chart-head__tf-label">{tfLabel}</span>
            <span className="chart-head__menu-mark" aria-hidden />
          </button>
          {tfMenu}
        </div>
      ) : (
        <div className="chart-head__intervals" role="group" aria-label="Timeframe">
          {INTERVALS.map(({ res: r, label, menuLabel }) => (
            <button
              key={r}
              type="button"
              className={`chart-head__tf${res === r ? " is-active" : ""}`}
              title={menuLabel || label}
              aria-label={menuLabel || label}
              aria-pressed={res === r}
              onClick={() => pick(r)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {tradeSlot ? (
        <>
          <span className="chart-head__sep" aria-hidden />
          <div className="chart-head__trade">{tradeSlot}</div>
        </>
      ) : null}

      {showZoom && (
        <>
          <span className="chart-head__sep" aria-hidden />
          <div className="chart-head__zoom" role="group" aria-label="Zoom">
            <button
              type="button"
              className="chart-head__btn chart-head__btn--icon chart-head__btn--zoom"
              title="Zoom in"
              aria-label="Zoom in"
              onClick={() => zoomIn()}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
                <path fill="currentColor" d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5z" />
              </svg>
            </button>
            <button
              type="button"
              className="chart-head__btn chart-head__btn--icon chart-head__btn--zoom"
              title="Zoom out"
              aria-label="Zoom out"
              onClick={() => zoomOut()}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
                <path fill="currentColor" d="M5 11h14v2H5v-2z" />
              </svg>
            </button>
          </div>
          <span className="chart-head__sep" aria-hidden />
        </>
      )}

      <div className="chart-head__tools" role="group" aria-label="Chart tools">
        <button
          type="button"
          className="chart-head__btn chart-head__btn--icon chart-head__btn--indicators"
          title="Indicators"
          aria-label="Indicators"
          onClick={() => {
            setStyleOpen(false);
            setVolOpen(false);
            setTfOpen(false);
            setIndicatorOpen(true);
          }}
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden>
            <path
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 17.5c1-.1 1.85-7.4 4.4-7.7 2.15-.25 2.7 6.5 5.1 4.95 1.85-1.2 2.75-5.6 5.3-5.5"
            />
            <path
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              d="M4.7 12h4.6"
            />
          </svg>
        </button>

        {!isMobileViewport && typeof onToggleMarketWatch === "function" ? (
          <>
            <span className="chart-head__sep chart-head__sep--tight" aria-hidden />
            <button
              type="button"
              className={`chart-head__btn chart-head__btn--icon chart-head__btn--marketwatch${!marketWatchHidden ? " is-active" : ""}`}
              title={
                marketWatchHidden
                  ? "Show Market Watch (Ctrl + M)"
                  : "Hide Market Watch (Ctrl + M)"
              }
              aria-label={marketWatchHidden ? "Show Market Watch" : "Hide Market Watch"}
              aria-pressed={!marketWatchHidden}
              onClick={onToggleMarketWatch}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
                <rect
                  x="4"
                  y="3.5"
                  width="16"
                  height="17"
                  rx="1.5"
                  stroke="currentColor"
                  strokeWidth="1.65"
                />
                <path
                  stroke="currentColor"
                  strokeWidth="1.65"
                  strokeLinecap="round"
                  d="M7.5 9h9M7.5 12.5h9M7.5 16h6"
                />
              </svg>
            </button>
          </>
        ) : null}
      </div>

      {!isMobileViewport ? <div className="chart-head__spacer" aria-hidden /> : null}

      {trailing ? <div className="chart-head__trailing">{trailing}</div> : null}

      {showEndIcons ? (
        <div className="chart-head__icons chart-head__icons--end">
          <button
            type="button"
            className="chart-head__btn chart-head__btn--icon chart-head__btn--camera"
            title="Take a snapshot"
            aria-label="Take a snapshot"
            onClick={takeScreenshot}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden>
              <rect
                x="1.75"
                y="1.75"
                width="20.5"
                height="20.5"
                rx="1.25"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.35"
                strokeDasharray="1.9 1.55"
                strokeLinecap="butt"
              />
              <path
                fill="currentColor"
                fillRule="evenodd"
                d="M10.55 12.05h1l.45-.8h3.9l.45.8h1c.5 0 .9.4.9.9v4.7c0 .5-.4.9-.9.9h-6.8c-.5 0-.9-.4-.9-.9v-4.7c0-.5.4-.9.9-.9zm3.4 1.2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 1.05a.95.95 0 1 1 0 1.9.95.95 0 0 1 0-1.9z"
              />
            </svg>
          </button>

          <button
            type="button"
            className={`chart-head__btn chart-head__btn--icon chart-head__btn--expand${isFullscreen ? " is-active" : ""}`}
            title={isFullscreen ? "Exit full screen" : "Full screen"}
            aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
            aria-pressed={isFullscreen}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden>
                <path d="M8.6 2.8v5.9H2.7v1.85h7.75V2.8H8.6zm7.65 0h-1.85v7.75H21.3V8.7h-5.05V2.8zM2.7 13.45v1.85h5.9v5.9h1.85v-7.75H2.7zm18.6 0h-7.75v7.75h1.85v-5.9h5.9v-1.85z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden>
                <path d="M10.2 3H3v7.2h1.9V6.24L9.96 11.3l1.34-1.34L6.24 4.9H10.2V3z" />
                <path d="M13.8 3v1.9h3.96l-5.06 5.06 1.34 1.34L19.1 6.24V10.2H21V3h-7.2z" />
                <path d="M3 13.8v7.2h7.2v-1.9H6.24l5.06-5.06-1.34-1.34L4.9 17.76V13.8H3z" />
                <path d="M14.04 12.66 12.7 14l5.06 5.06H13.8V21H21v-7.2h-1.9v3.96l-5.06-5.1z" />
              </svg>
            )}
          </button>
        </div>
      ) : null}

      {indicatorOpen ? (
        <SelectIndicatorDialog darkTheme={isDark} onClose={() => setIndicatorOpen(false)} />
      ) : null}
    </div>
  );
}
